import React, { useState, memo } from 'react';
import { X, Heart, MessageSquare, Gift, Camera, Lock, Video, Mic, BarChart2, Bookmark, Share2 } from 'lucide-react';
interface ViewerPersona {
  id: string;
  name: string;
  emoji: string;
  emojiBackground: string;
  type: string;
  description: string;
  lastAction: string;
  lastActionTime: string;
  metrics: {
    emotionalBond: number;
    upsellPotential: number;
    reEngagement: number;
    memoryTrigger: number;
  };
  performanceMetrics: {
    profileViews: number;
    timePerPost: string;
    interactionRate: number;
    conversionRate: number;
  };
  conversionEvents: {
    vipSubscribed: boolean;
    sentGift: boolean;
    boughtPhotoSet: boolean;
    commented: boolean;
  };
  moods: string[];
  primaryMood: string;
  message: string;
  hasEmotionalSpike: boolean;
  hasUpsellOpportunity: boolean;
  isHighSpender: boolean;
  hasMemoryTrigger: boolean;
  upsells: {
    description: string;
    match: number;
  }[];
  purchaseHistory: {
    item: string;
    price: string;
    date: string;
  }[];
  posts: {
    type: string;
    content: string;
    engagement: number;
    isPremium: boolean;
  }[];
}
interface ProfilePreviewModalProps {
  persona: ViewerPersona;
  relationshipDay: number;
  onClose: () => void;
}
export function ProfilePreviewModal({
  persona,
  relationshipDay,
  onClose
}: ProfilePreviewModalProps) {
  const [activeTab, setActiveTab] = useState('feed');
  const [showHeatmap, setShowHeatmap] = useState(false);
  // Define heatmap areas based on persona type
  const getHeatmapAreas = () => {
    if (persona.id === 'camwhale92') {
      return [{
        top: '20%',
        left: '50%',
        size: '80px',
        intensity: 0.8
      }, {
        top: '40%',
        left: '50%',
        size: '120px',
        intensity: 0.6
      }, {
        top: '55%',
        left: '30%',
        size: '100px',
        intensity: 0.9
      }, {
        top: '75%',
        left: '50%',
        size: '200px',
        intensity: 0.7
      } // Premium content
      ];
    } else if (persona.id === 'lonelyheart84') {
      return [{
        top: '20%',
        left: '50%',
        size: '80px',
        intensity: 0.7
      }, {
        top: '40%',
        left: '50%',
        size: '120px',
        intensity: 0.9
      }, {
        top: '55%',
        left: '70%',
        size: '100px',
        intensity: 0.8
      }, {
        top: '75%',
        left: '50%',
        size: '200px',
        intensity: 0.6
      } // Voice notes
      ];
    } else if (persona.id === 'curiouscouple') {
      return [{
        top: '20%',
        left: '50%',
        size: '80px',
        intensity: 0.6
      }, {
        top: '40%',
        left: '50%',
        size: '120px',
        intensity: 0.7
      }, {
        top: '55%',
        left: '30%',
        size: '100px',
        intensity: 0.8
      }, {
        top: '55%',
        left: '70%',
        size: '100px',
        intensity: 0.7
      }, {
        top: '75%',
        left: '50%',
        size: '200px',
        intensity: 0.9
      } // Interactive content
      ];
    } else {
      return [{
        top: '20%',
        left: '50%',
        size: '80px',
        intensity: 0.7
      }, {
        top: '40%',
        left: '50%',
        size: '120px',
        intensity: 0.8
      }, {
        top: '75%',
        left: '50%',
        size: '200px',
        intensity: 0.6
      } // General content
      ];
    }
  };
  // Generate personalized feed items
  const getPersonalizedFeed = () => {
    // Base feed items
    const feedItems = [{
      type: 'photo',
      content: 'Just finished a new photoshoot 📸',
      engagement: 78,
      isPremium: false
    }, {
      type: 'text',
      content: "Good morning! How's everyone doing today?",
      engagement: 65,
      isPremium: false
    }, {
      type: 'poll',
      content: 'What should I post next? 1) Lingerie 2) Casual 3) Cosplay',
      engagement: 82,
      isPremium: false
    }];
    // Add persona-specific content
    if (persona.id === 'camwhale92') {
      feedItems.unshift({
        type: 'video',
        content: "Exclusive behind-the-scenes from today's toy unboxing 🎮",
        engagement: 92,
        isPremium: true
      });
    } else if (persona.id === 'lonelyheart84') {
      feedItems.unshift({
        type: 'voice',
        content: 'A special goodnight message just for you... 💤💕',
        engagement: 95,
        isPremium: true
      });
    } else if (persona.id === 'curiouscouple') {
      feedItems.unshift({
        type: 'video',
        content: 'New fantasy roleplay scenario - perfect for couples! 👫',
        engagement: 88,
        isPremium: true
      });
    } else if (persona.id === 'alphamale23') {
      feedItems.unshift({
        type: 'photo',
        content: 'Exclusive content - only for those who can handle it 🔥',
        engagement: 75,
        isPremium: true
      });
    }
    return feedItems;
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'feed':
        return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-5533">
            {getPersonalizedFeed().map((post, index) => <div key={index} className="p-4" data-id="element-5534">
                <div className="flex items-start gap-3" data-id="element-5535">
                  <div className="w-10 h-10 rounded-full overflow-hidden" data-id="element-5536">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vHHEJMHRsXTLdBi469pjxd/image.png" alt="Angel profile" className="w-full h-full object-cover" data-id="element-5537" />
                  </div>
                  <div className="flex-1" data-id="element-5538">
                    <div className="flex justify-between" data-id="element-5539">
                      <span className="font-medium text-sm" data-id="element-5540">
                        Your Angel Name
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-5541">2h ago</span>
                    </div>
                    <p className="text-sm mt-1" data-id="element-5542">{post.content}</p>
                    {/* Post preview */}
                    <div className="mt-2 rounded-lg bg-gray-100 dark:bg-gray-800 h-40 flex items-center justify-center" data-id="element-5543">
                      {post.type === 'photo' && <div className="flex flex-col items-center" data-id="element-5544">
                          <Camera size={24} className="text-gray-400" data-id="element-5545" />
                          <span className="text-xs text-gray-500 mt-1" data-id="element-5546">
                            Photo Post
                          </span>
                          {post.isPremium && <div className="mt-2 flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-1 rounded-full text-xs" data-id="element-5547">
                              <Lock size={12} data-id="element-5548" />
                              <span data-id="element-5549">Unlock Premium Content</span>
                            </div>}
                        </div>}
                      {post.type === 'video' && <div className="flex flex-col items-center" data-id="element-5550">
                          <Video size={24} className="text-gray-400" data-id="element-5551" />
                          <span className="text-xs text-gray-500 mt-1" data-id="element-5552">
                            Video Post
                          </span>
                          {post.isPremium && <div className="mt-2 flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-1 rounded-full text-xs" data-id="element-5553">
                              <Lock size={12} data-id="element-5554" />
                              <span data-id="element-5555">Unlock Premium Content</span>
                            </div>}
                        </div>}
                      {post.type === 'voice' && <div className="flex flex-col items-center" data-id="element-5556">
                          <Mic size={24} className="text-gray-400" data-id="element-5557" />
                          <span className="text-xs text-gray-500 mt-1" data-id="element-5558">
                            Voice Note
                          </span>
                          {post.isPremium && <div className="mt-2 flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-1 rounded-full text-xs" data-id="element-5559">
                              <Lock size={12} data-id="element-5560" />
                              <span data-id="element-5561">Unlock Premium Content</span>
                            </div>}
                        </div>}
                      {post.type === 'poll' && <div className="flex flex-col items-center" data-id="element-5562">
                          <BarChart2 size={24} className="text-gray-400" data-id="element-5563" />
                          <span className="text-xs text-gray-500 mt-1" data-id="element-5564">
                            Poll
                          </span>
                        </div>}
                      {post.type === 'text' && <div className="flex flex-col items-center" data-id="element-5565">
                          <MessageSquare size={24} className="text-gray-400" data-id="element-5566" />
                          <span className="text-xs text-gray-500 mt-1" data-id="element-5567">
                            Text Post
                          </span>
                        </div>}
                    </div>
                    {/* Engagement stats */}
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500" data-id="element-5568">
                      <span data-id="element-5569">{post.engagement}% engagement</span>
                      {post.isPremium && <span className="text-purple-500" data-id="element-5570">Premium Content</span>}
                    </div>
                    {/* Engagement buttons */}
                    <div className="flex gap-4 mt-3" data-id="element-5571">
                      <button className="text-gray-500 text-sm flex items-center gap-1" data-id="element-5572">
                        <Heart size={18} data-id="element-5573" />
                        <span data-id="element-5574">Like</span>
                      </button>
                      <button className="text-gray-500 text-sm flex items-center gap-1" data-id="element-5575">
                        <MessageSquare size={18} data-id="element-5576" />
                        <span data-id="element-5577">Comment</span>
                      </button>
                      <button className="text-gray-500 text-sm flex items-center gap-1" data-id="element-5578">
                        <Gift size={18} data-id="element-5579" />
                        <span data-id="element-5580">Tip</span>
                      </button>
                      <button className="text-gray-500 text-sm flex items-center gap-1 ml-auto" data-id="element-5581">
                        <Bookmark size={18} data-id="element-5582" />
                      </button>
                      <button className="text-gray-500 text-sm flex items-center gap-1" data-id="element-5583">
                        <Share2 size={18} data-id="element-5584" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>;
      case 'photos':
        return <div className="p-4" data-id="element-5585">
            <div className="grid grid-cols-3 gap-2" data-id="element-5586">
              {Array.from({
              length: 9
            }).map((_, index) => <div key={index} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center" data-id="element-5587">
                  <Camera className="text-gray-400" size={24} data-id="element-5588" />
                  {index < 3 && <div className="absolute top-1 right-1" data-id="element-5589">
                      <Lock className="text-purple-500" size={14} data-id="element-5590" />
                    </div>}
                </div>)}
            </div>
          </div>;
      case 'videos':
        return <div className="p-4" data-id="element-5591">
            <div className="grid grid-cols-2 gap-3" data-id="element-5592">
              {Array.from({
              length: 6
            }).map((_, index) => <div key={index} className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center" data-id="element-5593">
                  <Video className="text-gray-400" size={24} data-id="element-5594" />
                  {index < 2 && <div className="absolute top-1 right-1" data-id="element-5595">
                      <Lock className="text-purple-500" size={14} data-id="element-5596" />
                    </div>}
                </div>)}
            </div>
          </div>;
      case 'about':
        return <div className="p-4 space-y-4" data-id="element-5597">
            <div data-id="element-5598">
              <h3 className="text-sm font-medium mb-2" data-id="element-5599">About Me</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-5600">
                Hey there! I'm your fantasy Angel, ready to make all your dreams
                come true. I love connecting with my fans and creating special
                moments together. Subscribe to my VIP content for the full
                experience! 💋
              </p>
            </div>
            <div data-id="element-5601">
              <h3 className="text-sm font-medium mb-2" data-id="element-5602">Subscription Tiers</h3>
              <div className="space-y-2" data-id="element-5603">
                <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg" data-id="element-5604">
                  <div className="flex justify-between items-center" data-id="element-5605">
                    <span className="font-medium" data-id="element-5606">Basic</span>
                    <span className="text-sm" data-id="element-5607">$9.99/month</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-5608">
                    Access to regular posts and basic messaging
                  </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-100 dark:border-purple-800/50" data-id="element-5609">
                  <div className="flex justify-between items-center" data-id="element-5610">
                    <span className="font-medium text-purple-600 dark:text-purple-400" data-id="element-5611">
                      VIP
                    </span>
                    <span className="text-sm" data-id="element-5612">$29.99/month</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-5613">
                    Full access to all content, priority messaging, and
                    exclusive livestreams
                  </p>
                </div>
                <div className="bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg border border-pink-100 dark:border-pink-800/50" data-id="element-5614">
                  <div className="flex justify-between items-center" data-id="element-5615">
                    <span className="font-medium text-pink-600 dark:text-pink-400" data-id="element-5616">
                      Elite
                    </span>
                    <span className="text-sm" data-id="element-5617">$49.99/month</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-5618">
                    Everything in VIP plus personalized content, 1-on-1 calls,
                    and premium toy integrations
                  </p>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-5619">
      <div className="w-full max-w-md max-h-[90vh] bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200" data-id="element-5620">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-5621">
          <div className="flex items-center gap-2" data-id="element-5622">
            <div className={`w-8 h-8 rounded-full ${persona.emojiBackground} flex items-center justify-center text-white`} data-id="element-5623">
              <span data-id="element-5624">{persona.emoji}</span>
            </div>
            <div data-id="element-5625">
              <h3 className="font-medium text-sm" data-id="element-5626">Viewing as {persona.name}</h3>
              <p className="text-xs text-gray-500" data-id="element-5627">
                Day {relationshipDay} • {persona.type} type
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-5628">
            <button className={`p-2 rounded-md text-xs ${showHeatmap ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-800'}`} onClick={() => setShowHeatmap(!showHeatmap)} data-id="element-5629">
              {showHeatmap ? 'Hide Heatmap' : 'Show Heatmap'}
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors" data-id="element-5630">
              <X size={16} data-id="element-5631" />
            </button>
          </div>
        </div>
        {/* Profile */}
        <div className="relative overflow-y-auto flex-1" data-id="element-5632">
          {/* Heatmap overlay */}
          {showHeatmap && <div className="absolute inset-0 z-10 pointer-events-none" data-id="element-5633">
              {getHeatmapAreas().map((area, index) => <div key={index} className="absolute rounded-full bg-red-500 opacity-30 animate-pulse" style={{
            top: area.top,
            left: area.left,
            width: area.size,
            height: area.size,
            transform: 'translate(-50%, -50%)',
            opacity: area.intensity * 0.5
          }} data-id="element-5634" />)}
            </div>}
          {/* Profile header */}
          <div className="p-6 flex flex-col items-center border-b border-gray-200 dark:border-gray-800" data-id="element-5635">
            <div className="w-24 h-24 rounded-full overflow-hidden mb-3 border-2 border-purple-200 dark:border-purple-800" data-id="element-5636">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vHHEJMHRsXTLdBi469pjxd/image.png" alt="Angel profile" className="w-full h-full object-cover" data-id="element-5637" />
            </div>
            <h2 className="font-bold text-xl" data-id="element-5638">Your Angel Name</h2>
            <p className="text-sm text-gray-500" data-id="element-5639">@your_angel_handle</p>
            <div className="flex items-center gap-2 mt-2" data-id="element-5640">
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full" data-id="element-5641">
                VIP Creator
              </span>
              <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full" data-id="element-5642">
                Verified
              </span>
            </div>
            {/* Personalized bio based on viewer */}
            <p className="text-center text-sm mt-4 text-gray-600 dark:text-gray-400" data-id="element-5643">
              {persona.id === 'camwhale92' && 'Your #1 fantasy girl ready to play with all your favorite toys 🎮💜'}
              {persona.id === 'lonelyheart84' && "Here for you day and night. I'll be your shoulder to lean on 💕"}
              {persona.id === 'curiouscouple' && "Ready for adventure? Let's explore new fantasies together 👫✨"}
              {persona.id === 'shyteenfan' && 'Welcome! Take your time and enjoy the journey with me 😊'}
              {persona.id === 'alphamale23' && 'Only the best get VIP access. Are you up for the challenge? 🎯'}
            </p>
            {/* Stats */}
            <div className="flex justify-between w-full mt-4" data-id="element-5644">
              <div className="text-center" data-id="element-5645">
                <p className="font-bold" data-id="element-5646">128</p>
                <p className="text-xs text-gray-500" data-id="element-5647">Posts</p>
              </div>
              <div className="text-center" data-id="element-5648">
                <p className="font-bold" data-id="element-5649">15.4K</p>
                <p className="text-xs text-gray-500" data-id="element-5650">Followers</p>
              </div>
              <div className="text-center" data-id="element-5651">
                <p className="font-bold" data-id="element-5652">64</p>
                <p className="text-xs text-gray-500" data-id="element-5653">Following</p>
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex gap-3 w-full mt-4" data-id="element-5654">
              <button className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-md py-2 text-sm font-medium" data-id="element-5655">
                {persona.conversionEvents.vipSubscribed ? 'Renew VIP' : 'VIP Subscribe'}
              </button>
              <button className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-md py-2 text-sm font-medium" data-id="element-5656">
                Message
              </button>
              <button className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-md" data-id="element-5657">
                <Gift size={18} data-id="element-5658" />
              </button>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-800" data-id="element-5659">
            <button className={`flex-1 py-3 text-center text-sm font-medium relative ${activeTab === 'feed' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('feed')} data-id="element-5660">
              Feed
              {activeTab === 'feed' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400" data-id="element-5661"></div>}
            </button>
            <button className={`flex-1 py-3 text-center text-sm font-medium relative ${activeTab === 'photos' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('photos')} data-id="element-5662">
              Photos
              {activeTab === 'photos' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400" data-id="element-5663"></div>}
            </button>
            <button className={`flex-1 py-3 text-center text-sm font-medium relative ${activeTab === 'videos' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('videos')} data-id="element-5664">
              Videos
              {activeTab === 'videos' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400" data-id="element-5665"></div>}
            </button>
            <button className={`flex-1 py-3 text-center text-sm font-medium relative ${activeTab === 'about' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} onClick={() => setActiveTab('about')} data-id="element-5666">
              About
              {activeTab === 'about' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400" data-id="element-5667"></div>}
            </button>
          </div>
          {/* Tab content */}
          {renderTabContent()}
        </div>
        {/* Personalized upsell banner */}
        <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-id="element-5668">
          <div className="flex items-center justify-between" data-id="element-5669">
            <div data-id="element-5670">
              <p className="text-sm font-medium" data-id="element-5671">
                {persona.id === 'camwhale92' && 'Unlock VIP toy integration features!'}
                {persona.id === 'lonelyheart84' && 'Get daily voice messages with Elite tier!'}
                {persona.id === 'curiouscouple' && 'Try our couples fantasy package!'}
                {persona.id === 'shyteenfan' && 'Special newcomer discount - 50% off first month!'}
                {persona.id === 'alphamale23' && 'Exclusive content only for true VIPs!'}
              </p>
              <p className="text-xs opacity-80" data-id="element-5672">
                {persona.hasUpsellOpportunity ? 'Limited time offer!' : 'Upgrade now for more!'}
              </p>
            </div>
            <button className="px-3 py-1 bg-white text-purple-600 rounded text-sm font-medium" data-id="element-5673">
              Upgrade
            </button>
          </div>
        </div>
      </div>
    </div>;
}